<template>
  <div class="busca-instituicao" ref="container">
    <div class="campo-input">
      <input
        type="search"
        :value="texto"
        :placeholder="placeholder"
        autocomplete="off"
        @input="aoDigitar($event.target.value)"
        @focus="abrirPainel"
        @keydown.down.prevent="navegar(1)"
        @keydown.up.prevent="navegar(-1)"
        @keydown.enter.prevent="confirmarSelecaoAtiva"
      />
      <button
        v-if="texto"
        type="button"
        class="limpar"
        @click="limparSelecao"
        aria-label="Limpar seleção"
      >
        ×
      </button>
    </div>
    <ul v-if="aberto" class="lista" role="listbox">
      <li v-if="carregando" class="mensagem">Buscando instituições...</li>
      <template v-else>
        <li
          v-for="(instituicao, index) in resultados"
          :key="instituicao.idInstituicao || instituicao.instituicaoid || instituicao.id"
          :class="{ ativo: indiceAtivo === index }"
          role="option"
          @mousedown.prevent="selecionar(instituicao)"
        >
          <strong>{{ instituicao.nome }}</strong>
        </li>
        <li v-if="criando" class="mensagem">Cadastrando instituição...</li>
        <li
          v-else-if="podeCadastrar"
          class="acao"
          :class="{ ativo: indiceAtivo === resultados.length }"
          role="option"
          @mousedown.prevent="cadastrarInstituicao"
        >
          <strong>Adicionar "{{ texto.trim() }}"</strong>
          <small>Salvar esta instituição para reutilizar no módulo</small>
        </li>
        <li
          v-if="!resultados.length && !podeCadastrar && !criando"
          class="mensagem vazio"
        >
          Nenhuma instituição encontrada
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
import { buscarInstituicoesProjeto, criarInstituicaoProjeto } from '../services/api'

export default {
  name: 'BuscaInstituicaoGestaoProjetos',
  props: {
    value: {
      type: [Number, String, null],
      default: null
    },
    descricao: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Buscar instituição'
    }
  },
  data () {
    return {
      texto: '',
      consulta: '',
      resultados: [],
      carregando: false,
      criando: false,
      aberto: false,
      indiceAtivo: -1,
      instituicaoSelecionada: null,
      debounceId: null
    }
  },
  computed: {
    podeCadastrar () {
      const termo = (this.texto || '').trim()
      if (termo.length < 2) return false
      if (this.carregando || this.criando) return false
      const normalizado = termo.toLowerCase()
      return !this.resultados.some(item => (item?.nome || '').toLowerCase() === normalizado)
    }
  },
  watch: {
    descricao: {
      immediate: true,
      handler (novo) {
        if (!this.instituicaoSelecionada && !this.consulta) {
          this.texto = novo || ''
        }
      }
    },
    value (novo) {
      if (!novo) {
        this.instituicaoSelecionada = null
        if (!this.consulta) {
          this.texto = ''
        }
      }
    }
  },
  mounted () {
    document.addEventListener('click', this.verificarCliqueExterno)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.verificarCliqueExterno)
    clearTimeout(this.debounceId)
  },
  methods: {
    abrirPainel () {
      this.aberto = true
      if (!this.resultados.length && this.texto.trim().length >= 2) {
        this.buscarInstituicoes()
      }
    },
    aoDigitar (valor) {
      this.texto = valor
      this.consulta = valor
      this.aberto = true
      this.indiceAtivo = -1
      clearTimeout(this.debounceId)
      if (!valor) {
        this.resultados = []
        this.$emit('input', null)
        this.$emit('instituicao-selecionada', null)
        return
      }
      if (valor.trim().length < 2) {
        this.resultados = []
        return
      }
      this.debounceId = setTimeout(() => {
        this.buscarInstituicoes()
      }, 250)
    },
    async buscarInstituicoes () {
      const termo = (this.consulta || '').trim()
      if (!termo || termo.length < 2) return
      try {
        this.carregando = true
        const { data } = await buscarInstituicoesProjeto({ busca: termo })
        const lista = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : []
        this.resultados = lista.slice(0, 20).map(item => ({
          ...item,
          nome: item.nome || item.Nome || item.nomeInstituicao || item.descricao || ''
        }))
      } catch (error) {
        this.resultados = []
        // eslint-disable-next-line no-console
        console.error('[gestao-projetos] busca-instituicao-modulo', error)
        this.$toasted && this.$toasted.error('Não foi possível buscar as instituições cadastradas.')
      } finally {
        this.carregando = false
      }
    },
    async cadastrarInstituicao () {
      const nome = (this.texto || '').trim()
      if (!nome || nome.length < 2 || this.criando) return
      try {
        this.criando = true
        const { data } = await criarInstituicaoProjeto({ nome })
        if (data) {
          const id = data.idInstituicao || data.id
          this.resultados = [
            data,
            ...this.resultados.filter(item => (item.idInstituicao || item.id) !== id)
          ]
          this.selecionar({ ...data, nome: data.nome || nome })
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[gestao-projetos] cadastrar-instituicao-modulo', error)
        this.$toasted && this.$toasted.error('Não foi possível cadastrar a instituição informada.')
      } finally {
        this.criando = false
      }
    },
    selecionar (instituicao) {
      this.instituicaoSelecionada = instituicao
      this.texto = instituicao?.nome || ''
      this.aberto = false
      this.indiceAtivo = -1
      this.$emit('input', instituicao?.idInstituicao || instituicao?.instituicaoid || instituicao?.id || null)
      this.$emit('instituicao-selecionada', instituicao)
    },
    limparSelecao () {
      this.texto = ''
      this.consulta = ''
      this.instituicaoSelecionada = null
      this.resultados = []
      this.aberto = false
      this.indiceAtivo = -1
      this.$emit('input', null)
      this.$emit('instituicao-selecionada', null)
    },
    verificarCliqueExterno (evento) {
      if (!this.$refs.container) return
      if (!this.$refs.container.contains(evento.target)) {
        this.aberto = false
        this.indiceAtivo = -1
      }
    },
    navegar (direcao) {
      if (!this.aberto) return
      const total = this.resultados.length + (this.podeCadastrar ? 1 : 0)
      if (total === 0) return
      const novoIndice = this.indiceAtivo + direcao
      if (novoIndice < 0) {
        this.indiceAtivo = total - 1
      } else if (novoIndice >= total) {
        this.indiceAtivo = 0
      } else {
        this.indiceAtivo = novoIndice
      }
    },
    confirmarSelecaoAtiva () {
      if (this.indiceAtivo === this.resultados.length && this.podeCadastrar) {
        this.cadastrarInstituicao()
        return
      }
      if (this.indiceAtivo >= 0 && this.resultados[this.indiceAtivo]) {
        this.selecionar(this.resultados[this.indiceAtivo])
        return
      }
      if (this.podeCadastrar) {
        this.cadastrarInstituicao()
      }
    }
  }
}
</script>

<style scoped>
.busca-instituicao {
  position: relative;
}

.campo-input {
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #dbe3f0;
  border-radius: 8px;
  padding: 0 8px;
}

.campo-input input {
  flex: 1;
  border: none;
  outline: none;
  padding: 8px 6px;
  font-size: 0.9rem;
  background: transparent;
  color: #0f172a;
}

.campo-input input::placeholder {
  color: #94a3b8;
}

.campo-input .limpar {
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 1rem;
  cursor: pointer;
  padding: 4px;
}

.lista {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #dbe3f0;
  border-radius: 8px;
  max-height: 220px;
  overflow-y: auto;
  z-index: 12;
  padding: 4px 0;
  list-style: none;
  margin: 0;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.lista li {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.lista li strong {
  font-weight: 600;
  color: #0f172a;
}

.lista li small {
  color: #475569;
  font-size: 0.75rem;
}

.lista li.ativo,
.lista li:hover {
  background: #e8f1ff;
}

.acao {
  border-top: 1px solid #e2e8f0;
  color: #1d4ed8;
}

.acao strong {
  color: #1d4ed8;
}

.acao small {
  color: #475569;
}

.mensagem {
  padding: 10px 12px;
  color: #64748b;
  font-size: 0.8rem;
}

.mensagem.vazio {
  font-style: italic;
  color: #94a3b8;
}
</style>
